import React, { useState } from 'react';
import styled from '@emotion/styled';
import { aigleColor } from '@styles/colorStyleGuide';
import { AigleInput } from './AigleInput';
import { AiglePasswordInput } from './AiglePasswordInput';
import { AigleSelect } from './AigleSelect';
import { AigleSearchInput } from './AigleSearchInput';
import { AigleCheckbox } from '../Checkbox/AigleCheckbox';
import { AigleRadio, AigleRadioGroup } from '../Checkbox/AigleRadio';

/**
 * Input Style Guide Example Component
 * 
 * 모든 Input 관련 컴포넌트들의 사용 예시를 보여주는 컴포넌트입니다.
 * 디자인 시스템 문서화 및 테스트 용도로 사용됩니다.
 */

// Styled Components
const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 40px;
  background-color: ${aigleColor.background.paper};
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: ${aigleColor.text.primary};
  margin-bottom: 16px;
`;

const Description = styled.p`
  font-size: 16px;
  color: ${aigleColor.text.secondary};
  margin-bottom: 48px;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 56px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${aigleColor.text.primary};
  margin-bottom: 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
`;

const ExampleCard = styled.div`
  background-color: ${aigleColor.background.default};
  border: 1px solid ${aigleColor.border.default};
  border-radius: 8px;
  padding: 24px;
`;

const ExampleTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${aigleColor.text.secondary};
  margin-bottom: 12px;
  text-transform: uppercase;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${aigleColor.border.default};
  margin: 40px 0;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InlineRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const InputStyleGuideExample: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [gender, setGender] = useState('');
  const [textWithCounter, setTextWithCounter] = useState('');

  const selectOptions = [
    { value: '1', label: 'Options01' },
    { value: '2', label: 'Options02' },
    { value: '3', label: 'Options03' },
    { value: '4', label: 'Options04' },
  ];

  return (
    <Container>
      <Title>Input</Title>
      <Description>
        텍스트, 옵션 선택, 검색 등 다양한 형태로 제공되며 사용자의 정보 입력과 인터랙션을 지원합니다.
      </Description>

      {/* Basic Input Section */}
      <Section>
        <SectionTitle>Basic</SectionTitle>
        
        <Grid>
          {/* Default State */}
          <ExampleCard>
            <ExampleTitle>Default</ExampleTitle>
            <Row>
              <AigleInput placeholder="Placeholder" />
              <AigleInput placeholder="Placeholder" helperText="Basic Message" />
              <AigleInput label="Label" placeholder="Placeholder" />
            </Row>
          </ExampleCard>

          {/* Active State */}
          <ExampleCard>
            <ExampleTitle>Active</ExampleTitle>
            <Row>
              <AigleInput placeholder="Active" state="active" />
              <AigleInput placeholder="Active" state="active" helperText="Basic Message" />
              <AigleInput label="Label" placeholder="Active" state="active" />
            </Row>
          </ExampleCard>

          {/* Error State */}
          <ExampleCard>
            <ExampleTitle>Error</ExampleTitle>
            <Row>
              <AigleInput placeholder="Error" error />
              <AigleInput placeholder="Error" error helperText="Error Message" />
              <AigleInput label="Label" placeholder="Error" error />
            </Row>
          </ExampleCard>

          {/* Disabled State */}
          <ExampleCard>
            <ExampleTitle>Disabled</ExampleTitle>
            <Row>
              <AigleInput placeholder="Disabled" disabled />
              <AigleInput placeholder="Disabled" disabled helperText="Basic Message" />
              <AigleInput label="Label" placeholder="Disabled" disabled />
            </Row>
          </ExampleCard>
        </Grid>
      </Section>

      <Divider />

      {/* Options (Select) Section */}
      <Section>
        <SectionTitle>Options</SectionTitle>
        
        <Grid>
          <ExampleCard>
            <ExampleTitle>Default</ExampleTitle>
            <AigleSelect
              options={selectOptions}
              placeholder="Options"
              value={selectedOption}
              onChange={(value) => setSelectedOption(String(value))}
            />
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>With Label</ExampleTitle>
            <AigleSelect
              label="Label"
              options={selectOptions}
              placeholder="Options"
            />
          </ExampleCard>
        </Grid>
      </Section>

      <Divider />

      {/* Count (Character Counter) Section */}
      <Section>
        <SectionTitle>Count</SectionTitle>
        
        <Grid>
          <ExampleCard>
            <ExampleTitle>With Counter</ExampleTitle>
            <AigleInput
              placeholder="Up to 500"
              value={textWithCounter}
              onChange={(e) => setTextWithCounter(e.target.value)}
              maxLength={500}
              showCounter
            />
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>Error State</ExampleTitle>
            <AigleInput
              placeholder="ABCDEFGHI"
              value="ABCDEFGHI"
              maxLength={500}
              showCounter
              error
              helperText="Error"
            />
          </ExampleCard>
        </Grid>
      </Section>

      <Divider />

      {/* Sign (Login) Section */}
      <Section>
        <SectionTitle>Sign</SectionTitle>
        
        <Grid>
          <ExampleCard>
            <ExampleTitle>Empty</ExampleTitle>
            <Row>
              <AigleInput label="ID" placeholder="ID" />
              <AiglePasswordInput label="Password" placeholder="Password" />
            </Row>
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>Filled</ExampleTitle>
            <Row>
              <AigleInput 
                label="ID" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ID@email.com"
              />
              <AiglePasswordInput 
                label="Password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
              />
            </Row>
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>Password Visible</ExampleTitle>
            <Row>
              <AiglePasswordInput placeholder="Password" defaultVisible={false} />
              <AiglePasswordInput placeholder="1234" value="1234" defaultVisible={true} />
            </Row>
          </ExampleCard>
        </Grid>
      </Section>

      <Divider />

      {/* Floating Label Section */}
      <Section>
        <SectionTitle>Floating Label (MUI Style)</SectionTitle>
        
        <Grid>
          <ExampleCard>
            <ExampleTitle>Default</ExampleTitle>
            <Row>
              <AigleInput 
                label="이메일" 
                floatingLabel
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <AiglePasswordInput 
                label="비밀번호" 
                floatingLabel
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Row>
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>With Error</ExampleTitle>
            <Row>
              <AigleInput 
                label="이메일" 
                floatingLabel
                error
                helperText="올바른 이메일을 입력하세요"
              />
              <AiglePasswordInput 
                label="비밀번호" 
                floatingLabel
                error
                helperText="8자 이상 입력하세요"
              />
            </Row>
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>With Value</ExampleTitle>
            <Row>
              <AigleInput 
                label="이름" 
                floatingLabel
                value="홍길동"
              />
              <AigleInput 
                label="전화번호" 
                floatingLabel
                value="010-1234-5678"
              />
            </Row>
          </ExampleCard>
        </Grid>
      </Section>

      <Divider />

      {/* Search Section */}
      <Section>
        <SectionTitle>Search</SectionTitle>
        
        <Grid>
          <ExampleCard>
            <ExampleTitle>Default</ExampleTitle>
            <AigleSearchInput
              placeholder="Placeholder"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={(value) => console.log('Search:', value)}
            />
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>With Label</ExampleTitle>
            <AigleSearchInput
              label="Search"
              placeholder="검색어를 입력하세요"
              clickableIcon
              onSearch={(value) => console.log('Search:', value)}
            />
          </ExampleCard>
        </Grid>
      </Section>

      <Divider />

      {/* Checkbox Section */}
      <Section>
        <SectionTitle>Checkbox</SectionTitle>
        
        <Grid>
          <ExampleCard>
            <ExampleTitle>Default</ExampleTitle>
            <InlineRow>
              <AigleCheckbox label="체크박스" />
              <AigleCheckbox label="체크됨" defaultChecked />
              <AigleCheckbox label="비활성화" disabled />
            </InlineRow>
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>Indeterminate</ExampleTitle>
            <InlineRow>
              <AigleCheckbox label="부분 선택" indeterminate />
              <AigleCheckbox label="부분 선택 (비활성)" indeterminate disabled />
            </InlineRow>
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>Controlled</ExampleTitle>
            <AigleCheckbox
              label="로그인 저장"
              checked={rememberMe}
              onChange={(checked) => setRememberMe(checked)}
            />
            <p style={{ marginTop: '12px', fontSize: '14px', color: aigleColor.text.secondary }}>
              상태: {rememberMe ? '체크됨' : '체크 안됨'}
            </p>
          </ExampleCard>
        </Grid>
      </Section>

      <Divider />

      {/* Radio Section */}
      <Section>
        <SectionTitle>Radio</SectionTitle>
        
        <Grid>
          <ExampleCard>
            <ExampleTitle>Radio Group (Vertical)</ExampleTitle>
            <AigleRadioGroup
              name="gender"
              value={gender}
              onChange={(value) => setGender(String(value))}
            >
              <AigleRadio value="male" label="남성" />
              <AigleRadio value="female" label="여성" />
              <AigleRadio value="other" label="기타" />
            </AigleRadioGroup>
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>Radio Group (Horizontal)</ExampleTitle>
            <AigleRadioGroup
              name="agreement"
              direction="horizontal"
              gap={16}
            >
              <AigleRadio value="agree" label="동의" />
              <AigleRadio value="disagree" label="비동의" />
            </AigleRadioGroup>
          </ExampleCard>

          <ExampleCard>
            <ExampleTitle>Disabled Radio</ExampleTitle>
            <InlineRow>
              <AigleRadio name="disabled" value="1" label="옵션 1" disabled />
              <AigleRadio name="disabled" value="2" label="옵션 2" disabled checked />
            </InlineRow>
          </ExampleCard>
        </Grid>
      </Section>
    </Container>
  );
};

export default InputStyleGuideExample;

